import { Injectable, computed, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IPatientResponseDTO } from 'src/app/core/domain/patient';
import { PatientService } from 'src/app/core/services/patient.service';

const STORAGE_KEY = 'rettx.activePatientRettxid';

/**
 * Global, signal-backed active-patient state.
 *
 * Mirrors {@link PatientService.patients$} into a signal, exposes a writable
 * "active patient" and persists the selection to localStorage so it survives
 * reloads. Auto-selects the first patient when none is set, or reconciles the
 * selection when the patients list changes (e.g. after add/delete).
 *
 * Consumed by the global header pill (feat: multi-patient header pill) and any
 * future surface that needs a shared notion of "which patient am I viewing".
 * Deliberately lightweight — it does NOT fetch anything itself; population
 * happens through the existing PatientService flow.
 */
@Injectable({ providedIn: 'root' })
export class ActivePatientService {
  private patientService = inject(PatientService);
  private destroyRef = inject(DestroyRef);

  private readonly _patients = signal<IPatientResponseDTO[]>([]);
  private readonly _activeRettxid = signal<string | null>(this.readPersistedRettxid());

  readonly patients = this._patients.asReadonly();

  readonly activePatient = computed<IPatientResponseDTO | null>(() => {
    const id = this._activeRettxid();
    const list = this._patients();
    if (!id) return null;
    return list.find(p => p.rettxid === id) ?? null;
  });

  readonly hasMultiplePatients = computed<boolean>(() => this._patients().length >= 2);

  constructor() {
    this.patientService.patients$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(list => this.onPatientsChanged(list ?? []));
  }

  /** Set the active patient. Persists the choice to localStorage. */
  setActivePatient(patient: IPatientResponseDTO | null): void {
    const id = patient?.rettxid ?? null;
    this._activeRettxid.set(id);
    this.persistRettxid(id);
  }

  private onPatientsChanged(list: IPatientResponseDTO[]): void {
    this._patients.set(list);
    const currentId = this._activeRettxid();
    const stillPresent = currentId && list.some(p => p.rettxid === currentId);

    if (stillPresent) return;

    // Prefer auto-selecting the first patient so downstream consumers always
    // have a value when there is exactly one patient (single-patient users get
    // the pill hidden, but any consumer reading activePatient() still works).
    const next = list[0] ?? null;
    this._activeRettxid.set(next?.rettxid ?? null);
    this.persistRettxid(next?.rettxid ?? null);
  }

  private readPersistedRettxid(): string | null {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    } catch {
      return null;
    }
  }

  private persistRettxid(id: string | null): void {
    try {
      if (typeof localStorage === 'undefined') return;
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage may be unavailable (private mode, quota); selection still lives
      // in-memory for the session.
    }
  }
}
